import { useState, useEffect, useCallback } from "react";
import { Download, IndianRupee, CreditCard, Wallet } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({
    stripe: { count: 0, total_inr: 0 },
    razorpay: { count: 0, total_inr: 0 },
    combined: { count: 0, total_inr: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [filters, setFilters] = useState({ start_date: "", end_date: "", gateway: "all" });

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.start_date) params.append("start_date", filters.start_date);
    if (filters.end_date) params.append("end_date", filters.end_date);
    if (filters.gateway && filters.gateway !== "all") params.append("gateway", filters.gateway);
    return params.toString();
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = buildQuery();
      const [listRes, sumRes] = await Promise.all([
        adminApi.get(`/admin/payments${q ? `?${q}` : ""}`),
        adminApi.get(`/admin/payments/summary${filters.start_date || filters.end_date ? `?${new URLSearchParams({
          ...(filters.start_date && { start_date: filters.start_date }),
          ...(filters.end_date && { end_date: filters.end_date }),
        }).toString()}` : ""}`),
      ]);
      setPayments(listRes.data);
      setSummary(sumRes.data);
    } catch (err) {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  }, [buildQuery, filters.start_date, filters.end_date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportGST = async () => {
    setDownloading(true);
    try {
      const q = buildQuery();
      const res = await adminApi.get(`/admin/export/gst.csv${q ? `?${q}` : ""}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `gst_report_${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("GST report exported");
    } catch (err) {
      toast.error("Failed to export GST report");
    } finally {
      setDownloading(false);
    }
  };

  const cards = [
    { label: "Razorpay Revenue", value: summary.razorpay.total_inr, count: summary.razorpay.count, icon: Wallet, color: "bg-blue-500", testid: "summary-razorpay" },
    { label: "Stripe Revenue", value: summary.stripe.total_inr, count: summary.stripe.count, icon: CreditCard, color: "bg-purple-500", testid: "summary-stripe" },
    { label: "Total Revenue", value: summary.combined.total_inr, count: summary.combined.count, icon: IndianRupee, color: "bg-green-500", testid: "summary-combined" },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-payments-page">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h1 className="text-3xl font-serif">Payment History</h1>
          <Button
            variant="outline"
            onClick={handleExportGST}
            disabled={downloading}
            data-testid="export-gst-btn"
            className="gap-2"
          >
            <Download size={16} />
            {downloading ? "Downloading..." : "Export GST CSV"}
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {cards.map((c) => (
            <div key={c.label} data-testid={c.testid} className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{c.label}</p>
                  <p className="text-2xl font-bold">₹{c.value.toLocaleString("en-IN")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{c.count} payment{c.count !== 1 ? "s" : ""}</p>
                </div>
                <div className={`${c.color} p-3 rounded-lg`}>
                  <c.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">From</label>
              <Input
                type="date"
                data-testid="filter-start-date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">To</label>
              <Input
                type="date"
                data-testid="filter-end-date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">Gateway</label>
              <select
                data-testid="filter-gateway"
                value={filters.gateway}
                onChange={(e) => setFilters({ ...filters, gateway: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="all">All Gateways</option>
                <option value="razorpay">Razorpay</option>
                <option value="stripe">Stripe</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                variant="ghost"
                data-testid="clear-filters-btn"
                onClick={() => setFilters({ start_date: "", end_date: "", gateway: "all" })}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-serif mb-4">Completed Payments</h2>
          {loading ? (
            <div className="flex justify-center py-8"><div className="spinner"></div></div>
          ) : payments.length === 0 ? (
            <p className="text-muted-foreground text-center py-8" data-testid="no-payments">
              No completed payments match your filters yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payments-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-3">Date</th>
                    <th className="text-left py-3 px-3">Customer</th>
                    <th className="text-left py-3 px-3">Package</th>
                    <th className="text-left py-3 px-3">Amount (INR)</th>
                    <th className="text-left py-3 px-3">Gateway</th>
                    <th className="text-left py-3 px-3">Transaction ID</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.transaction_id || p.session_id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-3">{(p.created_at || "").slice(0, 10)}</td>
                      <td className="py-3 px-3">
                        <div className="font-medium">{p.customer_name}</div>
                        <div className="text-xs text-muted-foreground">{p.customer_email}</div>
                      </td>
                      <td className="py-3 px-3">{p.package_name || "—"}</td>
                      <td className="py-3 px-3 font-medium">₹{Number(p.amount_inr).toLocaleString("en-IN")}</td>
                      <td className="py-3 px-3">
                        <span className={`px-2 py-1 rounded-full text-xs capitalize ${
                          p.gateway === "razorpay"
                            ? "bg-blue-100 text-blue-700"
                            : p.gateway === "stripe"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-gray-100 text-gray-700"
                        }`}>
                          {p.gateway}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <code className="text-xs text-muted-foreground break-all">{p.transaction_id}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPayments;
